export type ModelPickerHost = "powerpoint" | "excel";
export type ModelPickerProvider = "openai" | "anthropic";
export type ModelThinkingEffort = "none" | "low" | "medium" | "high" | "xhigh" | "max";
export type OpenAIReasoningMode = "standard" | "pro";

export interface ModelPickerEntry {
  /** Stable value persisted by the picker. */
  readonly id: string;
  readonly label: string;
  readonly provider: ModelPickerProvider;
  /** Provider-specific model id sent on the wire. */
  readonly model: string;
  readonly hosts: readonly ModelPickerHost[];
  readonly allowedEfforts: readonly ModelThinkingEffort[];
  readonly defaultEffort: ModelThinkingEffort;
  /** Omitted for providers without OpenAI reasoning modes. */
  readonly allowedModes?: readonly OpenAIReasoningMode[];
  readonly defaultMode?: OpenAIReasoningMode;
  readonly maxOutputTokens?: number;
  /** Newer Claude models reject a forced named tool while adaptive thinking. */
  readonly forcedToolChoice: "supported" | "auto-only";
}

const BOTH_HOSTS = ["powerpoint", "excel"] as const;
const STANDARD_AND_PRO = ["standard", "pro"] as const;
const STANDARD_ONLY = ["standard"] as const;
const FRONTIER_EFFORTS = ["low", "medium", "high", "xhigh", "max"] as const;
const FLEXIBLE_EFFORTS = ["none", "low", "medium", "high", "xhigh", "max"] as const;
const GPT_5_5_EFFORTS = ["none", "low", "medium", "high", "xhigh"] as const;
const CLAUDE_MAX_OUTPUT_TOKENS = 64_000;

export const MODEL_PICKER_CATALOG = [
  {
    id: "gpt-6-astra",
    label: "GPT-6 Astra",
    provider: "openai",
    model: "gpt-6-astra",
    hosts: BOTH_HOSTS,
    allowedEfforts: FRONTIER_EFFORTS,
    defaultEffort: "medium",
    allowedModes: STANDARD_AND_PRO,
    defaultMode: "standard",
    forcedToolChoice: "supported",
  },
  {
    id: "gpt-6.1-sol",
    label: "GPT-6.1 Sol",
    provider: "openai",
    model: "gpt-6.1-sol",
    hosts: BOTH_HOSTS,
    allowedEfforts: FRONTIER_EFFORTS,
    defaultEffort: "medium",
    allowedModes: STANDARD_AND_PRO,
    defaultMode: "standard",
    forcedToolChoice: "supported",
  },
  {
    id: "gpt-6-sol",
    label: "GPT-6 Sol",
    provider: "openai",
    model: "gpt-6-sol",
    hosts: BOTH_HOSTS,
    allowedEfforts: FLEXIBLE_EFFORTS,
    defaultEffort: "medium",
    allowedModes: STANDARD_AND_PRO,
    defaultMode: "standard",
    forcedToolChoice: "supported",
  },
  {
    id: "gpt-6-luna",
    label: "GPT-6 Luna",
    provider: "openai",
    model: "gpt-6-luna",
    hosts: BOTH_HOSTS,
    allowedEfforts: FLEXIBLE_EFFORTS,
    defaultEffort: "medium",
    allowedModes: STANDARD_AND_PRO,
    defaultMode: "standard",
    forcedToolChoice: "supported",
  },
  {
    id: "gpt-5.6-sol",
    label: "GPT-5.6 Sol",
    provider: "openai",
    model: "gpt-5.6-sol",
    hosts: BOTH_HOSTS,
    allowedEfforts: FLEXIBLE_EFFORTS,
    defaultEffort: "medium",
    allowedModes: STANDARD_AND_PRO,
    defaultMode: "standard",
    forcedToolChoice: "supported",
  },
  {
    id: "gpt-5.6-terra",
    label: "GPT-5.6 Terra",
    provider: "openai",
    model: "gpt-5.6-terra",
    hosts: BOTH_HOSTS,
    allowedEfforts: FLEXIBLE_EFFORTS,
    defaultEffort: "medium",
    allowedModes: STANDARD_AND_PRO,
    defaultMode: "standard",
    forcedToolChoice: "supported",
  },
  {
    id: "gpt-5.6-luna",
    label: "GPT-5.6 Luna",
    provider: "openai",
    model: "gpt-5.6-luna",
    hosts: BOTH_HOSTS,
    allowedEfforts: FLEXIBLE_EFFORTS,
    defaultEffort: "medium",
    allowedModes: STANDARD_AND_PRO,
    defaultMode: "standard",
    forcedToolChoice: "supported",
  },
  {
    id: "gpt-5.5",
    label: "GPT-5.5",
    provider: "openai",
    model: "gpt-5.5-2026-04-23",
    hosts: BOTH_HOSTS,
    allowedEfforts: GPT_5_5_EFFORTS,
    defaultEffort: "medium",
    allowedModes: STANDARD_ONLY,
    defaultMode: "standard",
    forcedToolChoice: "supported",
  },
  {
    id: "claude-fable-5-1",
    label: "Claude Fable 5.1",
    provider: "anthropic",
    model: "claude-fable-5-1",
    hosts: BOTH_HOSTS,
    allowedEfforts: FRONTIER_EFFORTS,
    defaultEffort: "high",
    maxOutputTokens: CLAUDE_MAX_OUTPUT_TOKENS,
    forcedToolChoice: "auto-only",
  },
  {
    id: "claude-opus-5-5",
    label: "Claude Opus 5.5",
    provider: "anthropic",
    model: "claude-opus-5-5",
    hosts: BOTH_HOSTS,
    allowedEfforts: FRONTIER_EFFORTS,
    defaultEffort: "medium",
    maxOutputTokens: CLAUDE_MAX_OUTPUT_TOKENS,
    forcedToolChoice: "auto-only",
  },
  {
    id: "claude-sonnet-5-5",
    label: "Claude Sonnet 5.5",
    provider: "anthropic",
    model: "claude-sonnet-5-5",
    hosts: BOTH_HOSTS,
    allowedEfforts: FRONTIER_EFFORTS,
    defaultEffort: "high",
    maxOutputTokens: CLAUDE_MAX_OUTPUT_TOKENS,
    forcedToolChoice: "auto-only",
  },
  {
    id: "opus-5",
    label: "Claude Opus 5",
    provider: "anthropic",
    model: "claude-opus-5",
    hosts: BOTH_HOSTS,
    allowedEfforts: FRONTIER_EFFORTS,
    defaultEffort: "high",
    maxOutputTokens: CLAUDE_MAX_OUTPUT_TOKENS,
    forcedToolChoice: "supported",
  },
] as const satisfies readonly ModelPickerEntry[];

export function getModelPickerEntries(host: ModelPickerHost): readonly ModelPickerEntry[] {
  return MODEL_PICKER_CATALOG.filter((entry) =>
    (entry.hosts as readonly ModelPickerHost[]).includes(host)
  );
}

export function findModelPickerEntry(
  host: ModelPickerHost,
  idOrModel: string
): ModelPickerEntry | undefined {
  const value = idOrModel.trim();
  return getModelPickerEntries(host).find(
    (entry) => entry.id === value || entry.model === value
  );
}
